'use client';

import { DescriptionTooltip } from '@/components/form/dynamic-field/components/description-tooltip';
import { Badge } from '@/components/ui/badge';
import {
  getInvoiceStatusCore,
  type InvoiceStatusResult,
} from './invoice-status-logic';

/**
 * The exact fields the status derivation reads, spelled as on the matrix
 * `Invoice` (which satisfies this structurally). A caller holding another
 * invoice shape (e.g. construct-core's camelCase one) must map onto these
 * spellings explicitly — never cast — or the meta-driven branches silently
 * read `undefined`.
 */
export interface InvoiceStatusFields {
  readonly invoiceValuesMeta?: { readonly total_value: number } | null;
  readonly closedTime?: string | null;
  readonly debtInvoiceCounts?: {
    readonly unclosed_debt_invoices?: number;
    readonly kinds?: { readonly 0?: number; readonly 1?: number };
  } | null;
  readonly automaticDebtInvoicingPaused?: boolean | null;
  readonly reminderAmount?: number | null;
  readonly attestedDate?: string | null;
  readonly dueDate: string;
  readonly distributionDate?: string | null;
  readonly invoiceComputedMeta?: { readonly payments_sum?: number } | null;
  readonly invoiceNumber?: number | null;
  readonly ocrNumber?: string | null;
}

export function getInvoiceStatus(
  invoice: InvoiceStatusFields
): InvoiceStatusResult {
  // Thin mapper onto the extracted state machine (invoice-status-logic.ts)
  // so the DW Avier & Fakturor report shares the exact same branches.
  return getInvoiceStatusCore({
    totalValue: invoice.invoiceValuesMeta?.total_value ?? 0,
    closedTime: invoice.closedTime,
    unclosedDebtInvoices: invoice.debtInvoiceCounts?.unclosed_debt_invoices,
    sentDebtCount: invoice.debtInvoiceCounts?.kinds?.['1'],
    sentReminderCount: invoice.debtInvoiceCounts?.kinds?.['0'],
    automaticDebtInvoicingPaused: invoice.automaticDebtInvoicingPaused,
    reminderAmount: invoice.reminderAmount,
    attestedDate: invoice.attestedDate,
    distributionDate: invoice.distributionDate,
    dueDate: invoice.dueDate,
    paymentsSum: invoice.invoiceComputedMeta?.payments_sum,
    invoiceNumber: invoice.invoiceNumber,
    ocrNumber: invoice.ocrNumber,
  });
}

export function InvoiceStatus({ invoice }: { invoice: InvoiceStatusFields }) {
  const status = getInvoiceStatus(invoice);

  if (status.additionalInfo) {
    return (
      <Badge variant={status.badgeVariant}>
        {status.text}{' '}
        <DescriptionTooltip
          className='max-w-[200px] text-xs font-normal whitespace-normal'
          description={status.additionalInfo}
        />
      </Badge>
    );
  }

  return <Badge variant={status.badgeVariant}>{status.text}</Badge>;
}
